/* eslint-disable no-case-declarations */
import { Descendant } from 'slate';
import { jsx } from 'slate-hyperscript';
import { ElementTypes, MarkHtmlTypes } from 'src/types/CustomSlateTypes';
import { ContentMetadata } from 'src/types/DiscussionPost';
import { parse } from 'node-html-parser';

const TEXT_NODE_TYPE = 3;

const deserialize: (options: {
  element: HTMLElement,
  markAttributes?: { [key: string]: any },
  metadata?: ContentMetadata,
}) => Descendant[] | Descendant = ({ element, markAttributes = {}, metadata = {} }) => {
  const { mentions = {}, quotes = {} } = metadata;
  if (element.nodeType === TEXT_NODE_TYPE) {
    return jsx('text', markAttributes, element.textContent);
  }

  const nodeAttributes = { ...markAttributes };

  switch (element.nodeName) {
    case MarkHtmlTypes.bold:
      nodeAttributes.bold = true;
      break;
    case MarkHtmlTypes.italic:
      nodeAttributes.italic = true;
      break;
    case MarkHtmlTypes.underline:
      nodeAttributes.underline = true;
      break;
    case MarkHtmlTypes.strikethrough:
      nodeAttributes.strikethrough = true;
      break;
    default:
  }

  const children = Array.from(element.childNodes)
    .map((node) => deserialize({
      element: (node as HTMLElement),
      markAttributes: nodeAttributes,
      metadata,
    }))
    .flat();

  if (children.length === 0) {
    children.push(jsx('text', nodeAttributes, ''));
  }

  switch (element.nodeName) {
    case 'BODY':
      return jsx('fragment', {}, children);
    case 'UL':
      return jsx('element', { type: ElementTypes.bulletedList }, children);
    case 'OL':
      return jsx('element', { type: ElementTypes.numberedList }, children);
    case 'LI':
      return jsx('element', { type: ElementTypes.listItem }, children);
    case 'A':
      return jsx('element', { type: ElementTypes.link, url: element.getAttribute('href') }, children);
    case 'FORUMS-MENTION':
      const mentionId = element.getAttribute('id')!;
      if (mentions[mentionId]) {
        const { userId, username } = mentions[mentionId];
        return jsx('element', {
          type: ElementTypes.mention,
          username,
          userId,
        }, children);
      }
      return children;
    case 'IMG':
      return jsx('element', { type: ElementTypes.image, url: element.getAttribute('src') }, children);
    case 'P':
      return jsx('element', { type: ElementTypes.paragraph }, children);
    case 'FORUMS-QUOTE':
      const quoteId = element.getAttribute('id')!;
      if (quotes[quoteId]) {
        const {
          userId, username, content, avatarSrc, state, postId,
        } = quotes[quoteId];
        return jsx('element', {
          type: ElementTypes.quote,
          username,
          userId,
          avatarSrc,
          content,
          state,
          quoteId,
          postId,
        }, children);
      }
      return children;
    default:
      return children;
  }
};

const deserializeFromHtml: (options: {
  html?: string,
  metadata?: ContentMetadata,
}) => Descendant[] | undefined = ({ html, metadata }) => {
  if (!html && typeof html !== 'string') return undefined;
  const document = typeof window !== 'undefined'
    ? new DOMParser().parseFromString(html, 'text/html').body
    : parse(html);

  return deserialize({ element: document as HTMLElement, metadata }) as Descendant[];
};

export default deserializeFromHtml;
